import React, { useEffect, useRef, useState } from 'react';

/* -------- packages -------- */
import * as Yup from 'yup';
import { useFormik } from 'formik';
import { Form, Row, Col, FormGroup, Label, Button, FormFeedback, Input, InputGroup, Spinner } from 'reactstrap';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import { toast, ToastContainer } from 'react-toastify';
import { DatePicker } from 'reactstrap-date-picker';
/* -------- ------- -------- */

/* -------- intern functions -------- */
import { hasAllRights, hasRight } from '../../../utils/userTools';
import { getErrorFromResponse } from '../../../utils/utils';
/* -------- ------- -------- */

/**
 * Custom component to handle map centering when coordinates change
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array<number>} props.position - Array containing latitude and longitude coordinates [lat, lng]
 * @returns {null} - This component doesn't render anything
 */
const MapCenter = ({ position }) => {
  const map = useMap(); // Access Leaflet map instance
  
  useEffect(() => {
    // Update the map center position when position prop changes
    map.setView(position, map.getZoom());
  }, [position, map]);
  
  return null; // This component doesn't render anything itself
};

/**
 * Main DynamicForm component for handling various types of form fields dynamically
 * with validation, state management, and customizable appearance.
 * 
 * @component
 * @param {Object} props - Component props
 * @param {Array} props.fields - Array of form field configurations
 * @param {Object} props.initialValues - Initial values for the form fields
 * @param {Function} props.editFunction - Function to call on form submission for editing data
 * @param {Function} props.editPwdFunction - Function to call on form submission for editing password
 * @param {Function} props.handleCancelFunction - Function to call when cancel button is clicked
 * @param {boolean} props.isEdit - Boolean flag indicating if the form is in edit mode
 * @param {boolean} [props.isChecklistDo=false] - Boolean flag for checklist mode
 * @param {Object} props.error - Error object for form submission errors
 * @param {boolean} [props.withoutCancel=false] - Boolean flag to hide cancel button if true
 * @param {string|number} [props.preId=null] - Pre-defined ID to include in form values
 * @param {string} [props.preKey=null] - Key corresponding to the pre-defined ID
 * @param {string} props.prefListName - Name of the preference list for user rights
 * @param {boolean} [props.onlyOneField=false] - Flag to make form display only one field per row
 * @param {boolean} [props.isProfileView=false] - Flag indicating if form is used in profile view
 * @param {Function} [props.handleAfterSubmit=null] - Function to call after form submission
 * @param {Object} props.disposition - Custom field layout object
 * @returns {JSX.Element} - Rendered form component
 */
const DynamicForm = ({
  fields,
  setInitialValues,
  initialValues,
  editFunction,
  editPwdFunction,
  handleCancelFunction,
  isEdit,
  isChecklistDo = false,
  error,
  withoutCancel = false,
  preId = null,
  preKey = null,
  prefListName,
  onlyOneField = false,
  isProfileView = false,
  handleAfterSubmit = null,
  disposition = null,
  isSignupForm = false,
  id = null,
  isLoading = false,
  isDetailMode = false,
  isConfig = false,
  groupedRows = [],
  onRemoveGroup = null,
  onFormikReady = null,
  submitAndNavigatBtn = false,
  submitAndNavigatLabel = '',
  handleReturnValues = false,
}) => {

  const dispatch = useDispatch(); // Dispatch function from Redux
  const navigate = useNavigate(); // Navigate function from React Router
  const { t } = useTranslation(); // Translation function from react-i18next
  const user = useSelector(state => state.Login.user); // Access user data from Redux store
  const documents = useSelector(state => state.Documents);
  
  // États pour gérer les uploads
  const [pendingUploads, setPendingUploads] = useState(new Map());
  const [isProcessingSubmit, setIsProcessingSubmit] = useState(false);
  const uploadProcessedRef = useRef(new Set());

  /**
   * Determines if password fields should be shown based on user rights and current context
   * @constant
   * @type {boolean}
   */
  const shouldShowPasswordFields = !isEdit || user.id === initialValues.id || hasRight("user", "pwd", user);
  
  // State variables
  const [position, setPosition] = useState([initialValues.latitude || 0, initialValues.longitude || 0]);
  const [isStartModalOpen, setIsStartModalOpen] = useState(false);
  const [valuesSaved, setValuesSaved] = useState(null);

  /* Password visibility state management */
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordRepeat, setShowPasswordRepeat] = useState(false);

  const [savedValues, setSavedValues] = useState({});

  
  // Ajoutez ces états au début du composant
  const [allErrors, setAllErrors] = useState({});
  const [touchedFields, setTouchedFields] = useState({});

  const [debounceTimers, setDebounceTimers] = useState({});

  const [navigateDirectly, setNavigateDirectly] = useState(false);
  
  /**
   * Builds validation schema dynamically based on fields configuration
   * @returns {Object} - Yup validation schema object
   */
  const validationSchema = fields.reduce((schema, field) => {
    let validator;
    
    if (field.type === 'selectArray') {
      validator = Yup.array().of(Yup.string());
      if (field.required) {
        validator = validator.min(1, t('REQUIRED'));
      }
    } else {
      validator = Yup.string();
      if (field.required) {
        validator = validator.required(t('REQUIRED'));
      } else {
        validator = validator.nullable()
      }
      if (field.type === 'email') {
        validator = validator.email(t('EMAIL'));
      }
      if (field.type === 'number') {
        validator = Yup.number().typeError(t('NUMBER'));
        if (field.required) {
          validator = validator.required(t('REQUIRED'));
        } else {
          validator = validator.nullable();
        }
        if (field.min !== undefined) {
          validator = validator.min(field.min, t('MIN_NUMBER', { min: field.min }));
        }
      }
      if (field.type === 'float') {
        validator = validator.matches(/^\d*\.?\d*$/, t('FLOAT'));
      }
      if (field.type === 'password' && field.key === 'passwordRepeat') {
        validator = validator.oneOf([Yup.ref('password'), null], t('PASSWORD'));
      }
      if (field.type === 'letter') {
        validator = validator.matches(/^[a-zA-ZÀ-ÿ\- ']+$/, t('LETTER'));
      }
      if (field.regex) {
        const errorMessage = field.errorMessage || t('is');
        validator = validator.matches(new RegExp(field.regex), errorMessage);
      }
    }
    
    schema[field.key] = validator;
    return schema;
  }, {});

  // Fonction pour vérifier si une ligne contient des champs groupés
  const isGroupedRow = (row) => {
    return groupedRows.some(group => 
      group.fields.every(fieldKey => row.includes(fieldKey))
    );
  };

  // Fonction pour obtenir les informations du groupe pour une ligne donnée
  const getGroupInfo = (row) => {
    const group = groupedRows.find(group => 
      group.fields.every(fieldKey => row.includes(fieldKey))
    );
    return group ? { groupName: group.groupName, groupIndex: group.groupIndex } : null;
  };

  // Modified initialValues logic to handle initialValue attribute
  const getInitialFieldValue = (field) => {
  if (field.defaultValue !== undefined) {
    return field.defaultValue;
  }
  if(field.isInJson){
    const jsonValue = initialValues[field.isInJson];
    const parsedValue = typeof jsonValue === 'string' && jsonValue ? 
      JSON.parse(jsonValue) : 
      jsonValue || {};

    if (field.type === 'date') {
      return isEdit ? formatDate(parsedValue[field.key]) : formatDate(field.defaultValue);
    } else if (field.type === 'selectArray') {
      return parsedValue[field.key] || [];
    } else {
      return isEdit ? (parsedValue[field.key] || null) : (field.defaultValue || null);
    }
  } else {
    // Gérer les champs dépendants à l'initialisation
    if (field.isDependingOnValue && field.options && field.type !== 'select') {
      const parentFieldValue = initialValues[field.isDependingOnValue];
      const currentFieldValue = initialValues[field.key];
      
      // Si le champ est vide et qu'il y a une valeur parent
      if ((!currentFieldValue || currentFieldValue === '') && parentFieldValue) {
        const parentField = fields.find(f => f.key === field.isDependingOnValue);
        if (parentField && parentField.options) {
          const selectedParentOption = parentField.options.find(option => option.id === parentFieldValue);
          if (selectedParentOption && selectedParentOption[field.key] !== undefined) {
            return selectedParentOption[field.key];
          }
        }
      }
    }
    
    if (field.type === 'date') {
      return isEdit ? formatDate(initialValues[field.key]) : formatDate(field.defaultValue);
    } else if (field.type === 'selectArray') {
      return initialValues[field.key] || [];
    } else {
      return isEdit ? (initialValues[field.key] || null) : (field.defaultValue || null);
    }
  }
};

  const memoizedInitialValues = React.useMemo(() => {
    return fields.reduce((values, field) => {      
      if (!(field.key in values)) { // Ne modifie que les nouvelles clés
        values[field.key] = getInitialFieldValue(field);
      }
      return values;
    }, {...savedValues}); // Garde les valeurs existantes
  }, [fields, savedValues]); // Dépend uniquement de fields

  /**
   * Initialize Formik for form handling with validation, initial values, and submission logic
   */
  const formik = useFormik({
    initialValues: memoizedInitialValues,
    validationSchema: Yup.object(validationSchema),
    validateOnChange: false, // Désactive la validation automatique sur onChange
    validateOnBlur: true,
    onSubmit: values => {
      fields.forEach((field) => {
        if(field.isId && values[field.key] == '') {
          values[field.key] = null;
        }
        if(field.isInJson){
          if(!values[field.isInJson]){
            values[field.isInJson] = {};
          } else if (typeof values[field.isInJson] === 'string') {
            values[field.isInJson] = JSON.parse(values[field.isInJson]);
          }
          values[field.isInJson][field.key] = values[field.key];
        }
      });

      fields.forEach((field) => {
        if(field.isInJson){
          if (typeof values[field.isInJson] === 'object') {
            values[field.isInJson] = JSON.stringify(values[field.isInJson]) || '{}';
          }
        }
      });
      
      // Préserver l'ID et rowVersion existants
      if (initialValues && initialValues.id) {
        values.id = initialValues.id;
      }
      if (initialValues && initialValues.rowVersion != null && initialValues.rowVersion != undefined) {
        values.rowVersion = initialValues.rowVersion;
      }
      
      if(preId && preKey) {
        values[preKey] = preId;
      }

      if (setInitialValues) {
        setInitialValues(values);
      }

      // Vérifier s'il y a des fichiers à uploader
      const filesToUpload = fields.filter(field => 
        field.type === 'uploadDocument' && values[field.key]
      );
      
      if (filesToUpload.length > 0) {
        setIsProcessingSubmit(true);
        
        // Uploader tous les fichiers
        filesToUpload.forEach(field => {
          const file = values[field.key];
          // Utiliser le libellé du produit ou un nom par défaut
          const fileName = values.lib || values.name || `file_${field.key}`;
          handleFileUpload(field.key, file, fileName);
        });
      } else {
        // Pas de fichiers, soumission directe
        
        if((isEdit && (shouldShowPasswordFields && !hasRight("user","w", user))) || isProfileView) {
          dispatch(editPwdFunction(values, navigate, isProfileView));
        } else if (navigateDirectly) {
          dispatch(editFunction(values, navigate, navigateDirectly));
        } else {
          dispatch(editFunction(values, navigate));
        }
      }
      
      if (handleReturnValues) {
        handleReturnValues(values);
      }
      if(handleAfterSubmit) {
        handleAfterSubmit();
      }
    },
    enableReinitialize: true, // Allows reinitialization of form values when initialValues changes
  });

  useEffect(() => {
    if (formik.values) {
      setSavedValues(formik.values);
    }
  }, [formik.values]);

  /**
   * Sets initial form values when initialValues object changes
   */
  useEffect(() => {
    if (Object.keys(initialValues).length !== 0) {
      formik.setValues(fields.reduce((values, field) => {
        if(field.isInJson){
          const jsonValue = initialValues[field.isInJson];
          const parsedValue = typeof jsonValue === 'string' && jsonValue ? 
            JSON.parse(jsonValue) : 
            jsonValue || {};

          if (field.type === 'date') {
            values[field.key] = isEdit ? formatDate(parsedValue[field.key]) : formatDate(field.defaultValue);
          } else if (field.type === 'selectArray') {
            values[field.key] = parsedValue[field.key] || [];
          } else {
            values[field.key] = parsedValue[field.key] || field.defaultValue || null;
          }
          return values;
        } else {
          if (field.type === 'date') {
            values[field.key] = isEdit ? formatDate(initialValues[field.key]) : formatDate(field.defaultValue);
          } else if (field.type === 'selectArray') {
            values[field.key] = initialValues[field.key] || [];
          } else {
            values[field.key] = initialValues[field.key] || field.defaultValue || null;
          }
          return values;
        }
      }, {}));
    }
  }, [initialValues]);

  /**
   * Handles form errors based on API error responses
   */
  useEffect(() => {
    if (error && error.data && error.data.errors) {
      error.data.errors.forEach(err => {
        if (err.code && err.key) {
          formik.setFieldError(err.key, t(err.code));
        }
      });
    }
  }, [error]);

  /**
   * Handles form cancel button click
   * Either calls the provided cancel function or navigates back
   */
  const handleCancel = () => {
    if(handleCancelFunction) {
      handleCancelFunction();
    } else {
      navigate(-1); // Return to previous page
    }
  };

  /**
   * Creates custom styles for react-select component with error handling
   * 
   * @param {boolean} hasError - Whether the field has a validation error
   * @returns {Object} - Custom styles for react-select
   */
  const customSelectStyles = (hasError) => ({
    control: (provided) => ({
      ...provided,
      border: hasError ? '1px red solid' : '1px ' + provided.borderColor + ' solid',
      '&:hover': {
        borderColor: hasError ? 'red' : provided['&:hover'].borderColor
      },
      boxShadow: hasError ? '0 0 0 0.5px red' : provided.boxShadow,
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white !important',
    }),
  });

  /**
   * Updates map position when latitude or longitude form values change
   */
  useEffect(() => {
    const lat = parseFloat(formik.values.latitude);
    const lng = parseFloat(formik.values.longitude);
    if (!isNaN(lat) && !isNaN(lng)) {
      setPosition([lat, lng]);
    }
  }, [formik.values.latitude, formik.values.longitude]);

  // Filter visible fields
  const [visibleFields, setVisibleFields] = useState(fields.filter(field => !field.noShow))

  useEffect(() => {
    if(fields){
      setVisibleFields(fields.filter(field => !field.noShow));
    }
  },[fields]);

  useEffect(() => {
    // Update touched fields
    setTouchedFields(prev => ({
      ...prev,
      ...formik.touched
    }));

    // Capture new errors and merge them with previous ones
    if (formik.errors && Object.keys(formik.errors).length > 0) {
      setAllErrors(prev => {
        const newErrors = { ...prev };
        
        // Add or update errors for touched fields
        Object.keys(formik.errors).forEach(fieldKey => {
          if (formik.touched[fieldKey]) {
            newErrors[fieldKey] = formik.errors[fieldKey];
          }
        });
        
        // Remove errors for fields that are now valid AND touched
        Object.keys(prev).forEach(fieldKey => {
          if (formik.touched[fieldKey] && !formik.errors[fieldKey]) {
            delete newErrors[fieldKey];
          }
        });
        
        return newErrors;
      });
    } else {
      // If no new errors, clean errors for touched fields that are now valid
      setAllErrors(prev => {
        const newErrors = { ...prev };
        Object.keys(formik.touched).forEach(fieldKey => {
          if (formik.touched[fieldKey] && !formik.errors[fieldKey]) {
            delete newErrors[fieldKey];
          }
        });
        return newErrors;
      });
    }
  }, [formik.errors, formik.touched]);

  const getFieldError = (fieldKey) => {
    // Priority to current Formik errors, otherwise use preserved errors
    return formik.errors[fieldKey] || allErrors[fieldKey];
  };

  const hasFieldError = (fieldKey) => {
    return !!(formik.errors[fieldKey] || allErrors[fieldKey]);
  };

  const shouldShowError = (fieldKey) => {
    return (formik.touched[fieldKey] || touchedFields[fieldKey]) && hasFieldError(fieldKey);
  };

  const handleNumberKeyDown = (e) => {
    // Block letters e, E, +, -, and other unwanted special characters
    const invalidChars = ['e', 'E', '+', '-'];
    if (invalidChars.includes(e.key)) {
      e.preventDefault();
    }
  };

  const handlePostalAutoSelect = (field, value) => {
    if (!field.autoSelect || !value || value.length < 2) return;

    const { 
      getDepartmentCode, 
      findByDepartment, 
      agencies, 
      sectors, 
      targetFields,
      showNotification = false,
      notificationMessage,
      debounceMs = 300
    } = field.autoSelect;

    // Nettoyer le timer précédent s'il existe
    if (debounceTimers[field.key]) {
      clearTimeout(debounceTimers[field.key]);
    }

    // Créer un nouveau timer
    const timer = setTimeout(() => {
      const departmentCode = getDepartmentCode(value);
      
      if (departmentCode && agencies && sectors) {
        const result = findByDepartment(departmentCode, agencies, sectors);
        
        if (result.agency && targetFields.agencyId) {
          formik.setFieldValue(targetFields.agencyId, result.agency.id);
        }
        
        if (result.sector && targetFields.sectorId) {
          formik.setFieldValue(targetFields.sectorId, result.sector.id);
        }
      }
      
      // Nettoyer le timer du state
      setDebounceTimers(prev => {
        const newTimers = { ...prev };
        delete newTimers[field.key];
        return newTimers;
      });
    }, debounceMs);

    // Sauvegarder le timer
    setDebounceTimers(prev => ({
      ...prev,
      [field.key]: timer
    }));
  };
  
  const handleDependentFieldUpdate = (fieldKey, selectedValue) => {
    // Mettre à jour la valeur du champ sélectionné
    formik.setFieldValue(fieldKey, selectedValue);
    
    // Chercher tous les champs qui dépendent de ce champ (logique existante pour les selects)
    const dependentFields = fields.filter(field => 
      field.isDependingOnValue === fieldKey && 
      field.options && 
      field.type === 'select'
    );
    
    dependentFields.forEach(dependentField => {
      if (selectedValue) {
        const selectedOption = dependentField.options.find(option => option.id === selectedValue);
        
        if (selectedOption && selectedOption[dependentField.key] !== undefined) {
          formik.setFieldValue(dependentField.key, selectedOption[dependentField.key]);
        }
      } else {
        formik.setFieldValue(dependentField.key, '');
      }
    });

    // Ces champs se mettent à jour automatiquement quand le parent change
    const nonSelectDependentFields = fields.filter(field => 
      field.isDependingOnValue === fieldKey && 
      field.options && 
      field.type !== 'select'
    );
    
    nonSelectDependentFields.forEach(dependentField => {
      if (selectedValue) {
        const selectedOption = dependentField.options.find(option => option.id === selectedValue);
        
        if (selectedOption && selectedOption[dependentField.key] !== undefined) {
          formik.setFieldValue(dependentField.key, selectedOption[dependentField.key]);
        }
      } else {
        formik.setFieldValue(dependentField.key, '');
      }
    });
  };

  useEffect(() => {
    return () => {
      Object.values(debounceTimers).forEach(timer => {
        if (timer) clearTimeout(timer);
      });
    };
  }, []);

  useEffect(() => {
    if (onFormikReady && formik) {
      onFormikReady(formik);
    }
  }, [formik.values]);

  // Fonction pour gérer l'upload d'un fichier
  const handleFileUpload = (fieldKey, file, fileName) => {
    const uploadId = `${fieldKey}_${Date.now()}`;
    
    // Ajouter l'upload à la liste des uploads en attente
    setPendingUploads(prev => new Map(prev.set(uploadId, {
      fieldKey,
      fileName,
      originalFile: file
    })));

    const formData = new FormData();
    const renamedFile = new File([file], fileName + '.' + file.name.split('.').pop(), {
      type: file.type
    });
    
    formData.append('filedata', renamedFile);
    
    // Dispatch de l'upload avec un identifiant unique
    dispatch(uploadDocumentRequest(formData, {
      type: 'default', 
      params: getUploadParamsFromField(fieldKey) // Fonction pour déterminer les params selon le field
    }, {
      headers: {'Content-Type': 'multipart/form-data'},
      uploadId // Ajouter un ID pour identifier l'upload
    }));
  };

  // Fonction pour déterminer les paramètres d'upload selon le champ
  const getUploadParamsFromField = (fieldKey) => {
    const field = fields.find(f => f.key === fieldKey);
    return field?.uploadParams || 'default'; // Permettre de configurer les params par field
  };

  // Surveiller les uploads terminés
  useEffect(() => {
    if (documents.lastDocumentAdded && 
        documents.lastDocumentAdded[0]?.docOriginalname &&
        pendingUploads.size > 0 &&
        isProcessingSubmit) {
      
      const uploadedFileName = documents.lastDocumentAdded[0].docOriginalname.split('.')[0];
      
      // Trouver l'upload correspondant
      for (const [uploadId, uploadInfo] of pendingUploads) {
        if (uploadInfo.fileName === uploadedFileName && 
            !uploadProcessedRef.current.has(uploadId)) {
          
          // Marquer comme traité
          uploadProcessedRef.current.add(uploadId);
          
          // Ajouter le document au champ correspondant dans formik
          const currentDocuments = formik.values[`${uploadInfo.fieldKey}_documents`] || [];
          formik.setFieldValue(`${uploadInfo.fieldKey}_documents`, [
            ...currentDocuments,
            ...documents.lastDocumentAdded
          ]);
          
          // Retirer de la liste des uploads en attente
          setPendingUploads(prev => {
            const newMap = new Map(prev);
            newMap.delete(uploadId);
            return newMap;
          });
          
          break;
        }
      }
      
      // Si tous les uploads sont terminés, continuer avec la soumission
      if (pendingUploads.size <= 1) { // <= 1 car on vient de supprimer un élément
        proceedWithSubmission();
      }
    }
  }, [documents.lastDocumentAdded, pendingUploads, isProcessingSubmit]);

  // Fonction pour procéder à la soumission après tous les uploads
  const proceedWithSubmission = () => {
    const finalValues = { ...formik.values };
    
    // Ajouter tous les documents uploadés aux valeurs finales
    fields.forEach(field => {
      if (field.type === 'uploadDocument') {
        const documentsField = `${field.key}_documents`;
        if (finalValues[documentsField] && finalValues[documentsField].length > 0) {
          finalValues.documentsToAdd = finalValues.documentsToAdd || [];
          finalValues.documentsToAdd.push(...finalValues[documentsField]);
          
          // Nettoyer les champs temporaires
          delete finalValues[documentsField];
          delete finalValues[field.key]; // Retirer le fichier original
        }
      }
    });
    
    // Appeler la fonction de soumission avec les valeurs finales
    dispatch(editFunction(finalValues, navigate));
    
    // Reset des états
    setIsProcessingSubmit(false);
    setPendingUploads(new Map());
    uploadProcessedRef.current.clear();
  };

  /**
   * Render the form with all configured fields and buttons
   */
  return (
    <div className={(isProfileView) ? '' : 'tableBody card'}>
      <div className={(isProfileView) ? '' : 'card-body'}>
        <Form onSubmit={formik.handleSubmit} className={'customForm' + (isSignupForm) && ' form-horizontal signUp'}>
          <Row>
            {disposition ? (
              disposition.map((row, rowIndex) => {
                const isGrouped = isGroupedRow(row);
                const groupInfo = isGrouped ? getGroupInfo(row) : null;

                return (
                  <div key={rowIndex} className={isGrouped ? "grouped-row-container" : ""}>
                    {isGrouped && onRemoveGroup && groupInfo && (
                      <button
                        type="button"
                        className="group-remove-btn"
                        onClick={() => onRemoveGroup(groupInfo.groupName, groupInfo.groupIndex)}
                        title={t('removeGroup')}
                      >
                        <i className="mdi mdi-close"></i>
                      </button>
                    )}
                    <Row key={rowIndex} className="w-100">
                    {row.map((fieldKey, colIndex) => (
                      <Col key={colIndex} md={12 / row.length}>
                        {fieldKey ? (
                          // Find the field configuration that matches this key
                          (() => {
                            const field = visibleFields.find(f => f.key === fieldKey);
                            if (!field) return null;

                            return (shouldShowPasswordFields || (!['password', 'passwordRepeat'].includes(field.key))) ? (
                              <FormGroup>
                                <Label htmlFor={field.key} data-cy={'form-label-' + field.key}>
                                  {(field.key === 'password' && isEdit) ? t('changePassword') : 
                                  (field.key === 'passwordRepeat' && isEdit) ? t('changePasswordRepeat') : 
                                  (field.label) ? field.label : t(field.key)}{field.required && <span className="text-danger">*</span>}
                                </Label>
                                {/* Render different input types based on field.type */}
                                {(field.type === 'date') ? (
                                  <DatePicker
                                    name={field.key}
                                    id={field.key}
                                    onChange={(value, formattedValue) => {
                                      formik.setFieldValue(field.key, value); // Update field value in Formik
                                    }}
                                    onBlur={formik.handleBlur}
                                    value={formik.values[field.key]}
                                    invalid={shouldShowError(field.key)}
                                    placeholder={field.placeholder || ''}
                                    autoComplete="new-password"
                                    maxDate={field.max ? field.max.format() : null}
                                    disabled={field.isDisabled}
                                    dateFormat={"DD/MM/YYYY"}
                                    data-cy={'form-input-' + field.key}
                                  />
                                ) : (field.type === 'select') ? 
                                  ((isEdit && !hasRight(prefListName,"w", user)) || field.isDisabled ? (
                                    // Display select as read-only input when user doesn't have write rights
                                    <Input
                                      type={'text'}
                                      name={field.key}
                                      id={field.key}
                                      onChange={formik.handleChange}
                                      onBlur={formik.handleBlur}
                                      value={(field && field.fullOptions)
                                                ? (
                                                  field.fullOptions.find((el) => 
                                                    el.id == formik.values[field.key]
                                                  ) && field.fullOptions.find((el) => 
                                                    el.id == formik.values[field.key]
                                                  ).lib
                                                ) ? field.fullOptions.find((el) => 
                                                      el.id == formik.values[field.key]
                                                    ).lib
                                                  : formik.values[field.key]
                                                : (field && field.options)
                                                  ? (
                                                    field.options.find((el) => 
                                                      el.id == formik.values[field.key]
                                                    ) && field.options.find((el) => 
                                                      el.id == formik.values[field.key]
                                                    ).lib
                                                  ) ? field.options.find((el) => 
                                                      el.id == formik.values[field.key]
                                                    ).lib
                                                    : formik.values[field.key]
                                                  : formik.values[field.key]
                                              }
                                      invalid={shouldShowError(field.key)}
                                      placeholder={field.placeholder || ''}
                                      autoComplete='new-password'
                                      disabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (isProfileView && user.id === initialValues.id) ? false : (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                                      data-cy={'form-input-' + field.key}
                                    />
                                  ) : (
                                    // Render normal select dropdown when user has write rights
                                    <Select
                                      name={field.key}
                                      id={field.key}
                                      placeholder={field.placeholder || ''}
                                      onChange={(option) => handleDependentFieldUpdate(field.key, option ? option.value : null)}
                                      onBlur={() => formik.setFieldTouched(field.key, true)}
                                      classNamePrefix="react-select"
                                      className={"react-select-container" + (shouldShowError(field.key) ? ' is-invalid' : '')}
                                      value={formik.values[field.key] ? field.options
                                        .map(option => ({
                                          value: typeof option === 'object' ? option.id : option,
                                          label: t(typeof option === 'object' ? option.label : option)
                                        }))
                                        .find(option => option.value === formik.values[field.key]) : null}
                                      isDisabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                                      data-cy={'form-input-' + field.key}
                                      isClearable
                                      options={field.options
                                        .filter(option => {
                                        // Check if options should be filtered based on another field's value
                                        if (field.isDependingOn && 
                                          (formik.values[field.isDependingOn] != option[field.isDependingOn] && 
                                          (formik.values[field.isDependingOn] != null && 
                                            formik.values[field.isDependingOn] != undefined && 
                                            formik.values[field.isDependingOn] != ''
                                          )
                                          )) {
                                          return false;
                                        }

                                        // Handle dependent fields
                                        if (field.isDependingOnValue) {
                                          const dependingField = fields.find((el) => el.key == field.isDependingOnValue);
                                          const dependingFieldOptions = (dependingField && dependingField.options) ? dependingField.options : null;
                                          const dependingValue = (dependingFieldOptions && formik.values[field.isDependingOnValue]) 
                                                      ? dependingFieldOptions.find((el) => el.id == formik.values[field.isDependingOnValue]) 
                                                      : null;
                                          
                                          if((!dependingValue || (dependingValue && dependingValue[field.key] && dependingValue[field.key] != option.id)) && 
                                          (formik.values[field.isDependingOnValue] != null && 
                                            formik.values[field.isDependingOnValue] != undefined && 
                                            formik.values[field.isDependingOnValue] != '' 
                                          )
                                          ) {
                                          return false;
                                          }
                                        }

                                        // Handle N-to-N relationship dependencies
                                        if (field.isDependingOnArrayValue) {
                                          const dependingField = fields.find((el) => el.key == field.isDependingOnArrayValue);
                                          const dependingFieldOptions = (dependingField && dependingField.options) ? dependingField.options : null;
                                          const dependingValue = (dependingFieldOptions && formik.values[field.isDependingOnArrayValue]) 
                                                      ? dependingFieldOptions.find((el) => el.id == formik.values[field.isDependingOnArrayValue]) 
                                                      : null;
                                          const dependingArrayValues = (dependingValue) ? option[field.dependingOnArrayKey].includes(dependingValue.id) : null;
                                          
                                          if((!dependingValue || !dependingArrayValues) && 
                                          (formik.values[field.isDependingOnArrayValue] != null && 
                                            formik.values[field.isDependingOnArrayValue] != undefined && 
                                            formik.values[field.isDependingOnArrayValue] != '' 
                                          )
                                          ) {
                                          return false;
                                          }
                                        }

                                        return true;
                                        })
                                        .map(option => ({
                                        value: typeof option === 'object' ? option.id : option,
                                        label: t(typeof option === 'object' ? option.label : option)
                                        }))}
                                    />
                                  )
                                ) : (field.type === 'selectArray') ? (
                                  // Handle multi-select fields
                                  <>
                                    <Select
                                      isMulti
                                      name={field.key}
                                      id={field.key}
                                      options={field.options.map(option => ({ value: option.id, label: t(option.label) }))}
                                      className={"react-select-container" + (shouldShowError(field.key) ? ' is-invalid' : '')}
                                      classNamePrefix="select"
                                      styles={customSelectStyles(shouldShowError(field.key))}
                                      value={field.options.filter(option => formik.values[field.key].includes(option.id)).map(option => ({ value: option.id, label: t(option.label) }))}
                                      onChange={selectedOptions => formik.setFieldValue(field.key, selectedOptions ? selectedOptions.map(option => option.value) : [])}
                                      onBlur={() => formik.setFieldTouched(field.key, true)}
                                      disabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (isProfileView && user.id === initialValues.id) ? false : (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                                      placeholder={field.placeholder || ''}
                                      data-cy={'form-input-' + field.key}
                                    />
                                    {formik.touched[field.key] && formik.errors[field.key] && (
                                      <FormFeedback className="d-block">{formik.errors[field.key]}</FormFeedback>
                                    )}
                                  </>
                                ) : (field.type === 'button') ? (
                                  <div className="d-flex justify-content-start mb-3">
                                    <Button
                                      type="button" // Important pour éviter la soumission du formulaire
                                      color="primary"
                                      onClick={field.onClick}
                                      className="mt-2"
                                      data-cy={'form-button-' + field.key}
                                    >
                                      {field.label}
                                    </Button>
                                  </div>
                                ) : (field.type === 'selectGrouped') ? (
                                  // Handle multi-select fields
                                  <>
                                    <Select
                                      name={field.key}
                                      id={field.key}
                                      placeholder={field.placeholder || ''}
                                      options={field.options}
                                      classNamePrefix="react-select"
                                      className={"react-select-container" + (shouldShowError(field.key) ? ' is-invalid' : '')}
                                      styles={customSelectStyles(shouldShowError(field.key))}
                                      onChange={(selectedOption) => formik.setFieldValue(field.key, selectedOption ? selectedOption.value : '')}
                                      onBlur={() => formik.setFieldTouched(field.key, true)}
                                      value={(() => {
                                        const currentValue = formik.values[field.key];
                                        if (!currentValue) return null;
                                        
                                        // Parcourir toutes les options groupées pour trouver la correspondance
                                        for (const group of field.options) {
                                          const matchedOption = group.options.find(option => option.value == currentValue);
                                          if (matchedOption) {
                                            return matchedOption;
                                          }
                                        }
                                        return null;
                                      })()}
                                      isDisabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (isProfileView && user.id === initialValues.id) ? false : (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                                      formatGroupLabel={(data) => (
                                        <div className='selectGrouped-TitleGroup'>
                                          <span>{data.label}</span>
                                        </div>
                                      )}
                                      data-cy={'form-input-' + field.key}
                                    />
                                  </>
                                ) : (field.type === 'uploadDocument') ? (
                                  <>
                                  <div className="uploadDocumentContainer">
                                    <label htmlFor={`file-upload-${field.key}`} className="btn btn-primary">
                                      <i className='mdi mdi-paperclip'></i> {t('selectFile')}
                                    </label>
                                    
                                    <input
                                      id={`file-upload-${field.key}`}
                                      type="file"
                                      className="uploadFileInput"
                                      accept={field.docType}
                                      onChange={(e) => {
                                        const file = e.target.files[0];
                                        if (!file) return;
                                        
                                        let fileType = file.type.split('/')[0];
                                        let expectedType = field.docType.split('/')[0];

                                        if (fileType !== expectedType) {
                                          formik.setFieldValue(field.key, null);
                                          toast.warn(t(getErrorFromResponse({message: "fileTypeError"}, "generic")));
                                          return;
                                        }

                                        formik.setFieldValue(field.key, file);
                                      }} 
                                    />
                                    <p className="fileName">{formik.values[field.key] && formik.values[field.key].name}</p>

                                    {formik.values[field.key] && formik.values[field.key].type && formik.values[field.key].type.startsWith('image/') ? (
                                      <>
                                        <div className="deviderContainer">
                                          <span className="dividerSpan logo-lg"> </span>
                                        </div>
                                        <div className="imagePreview">
                                          <img 
                                            src={URL.createObjectURL(formik.values[field.key])} 
                                            alt="Preview" 
                                          />
                                        </div>
                                      </>
                                    ) : (
                                      <p>{t('noFileSelectioned')}</p>
                                    )}

                                    {/* Indicateur d'upload en cours */}
                                    {isProcessingSubmit && formik.values[field.key] && (
                                      <div className="upload-status">
                                        <Spinner size="sm" /> {t('uploading')}...
                                      </div>
                                    )}
                                  </div>
                                  </>
                                ) : (
                                  // Handle text inputs and password fields
                                  <div className={(field.type == 'password') ? "passwordField" : ''}>
                                    <InputGroup>
                                      {/* Affichage du startAdornment si présent */}
                                      {field.startAdornment && (
                                        <div>
                                          <span className="transparent-adornment">
                                            {field.startAdornment}
                                          </span>
                                        </div>
                                      )}
                                      <Input
                                        type={
                                          /* Change input type to text if showPassword or showPasswordRepeat are true */
                                          field.key == 'password' ? ((field.type !== 'password') 
                                                                    ? field.type 
                                                                    : (showPassword ? 'text': 'password'))
                                                                  : ((field.type !== 'password') 
                                                                    ? (field.type == 'float') 
                                                                      ? 'number'
                                                                      : field.type 
                                                                    : (showPasswordRepeat ? 'text': 'password'))}
                                        name={field.key}
                                        id={field.key}
                                        className={field.key}
                                        onChange={(e) => {
                                          formik.handleChange(e);
                                          // Vérifier si le champ a une configuration autoSelect
                                          if (field.autoSelect) {
                                            handlePostalAutoSelect(field, e.target.value);
                                          }
                                        }}
                                        onKeyDown={(e) => {
                                          if(field.type == 'number' || field.type == 'float') {
                                            handleNumberKeyDown(e);
                                          }
                                        }}
                                        onBlur={formik.handleBlur}
                                        min={field.type == 'number' ? field.min : undefined}
                                        step={field.type == 'float' ? (field.step || 0.5) : undefined}
                                        value={formik.values[field.key] ?? ''}
                                        invalid={shouldShowError(field.key)}
                                        placeholder={field.placeholder || ''}
                                        autoComplete='new-password'
                                        disabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (isProfileView && user.id === initialValues.id) ? false : (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                                        data-cy={'form-input-' + field.key}
                                      />
                                      
                                      {/* Affichage du endAdornment si présent */}
                                      {field.endAdornment && (
                                        <div>
                                          <span className="transparent-adornment">
                                            {field.endAdornment}
                                          </span>
                                        </div>
                                      )}
                                      {/* Show password toggle button for password field */}
                                      {(field.key === 'password') && (
                                        <span className='passwordVisibility' onClick={() => setShowPassword(!showPassword)}>
                                          <a id="projTogglePasswordVisibility" style={{ padding: 0 }}>
                                            <i className={showPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'} />
                                          </a>
                                        </span>
                                      )}
                                      {/* Show password toggle button for password repeat field */}
                                      {(field.key === 'passwordRepeat') && (
                                        <span className='passwordVisibility' onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}>
                                          <a id="projTogglePasswordVisibility" style={{ padding: 0 }}>
                                            <i className={showPasswordRepeat ? 'mdi mdi-eye' : 'mdi mdi-eye-off'} />
                                          </a>
                                        </span>
                                      )}
                                    </InputGroup>
                                  </div>
                                )}
                                {/* Password field specific feedback and instructions */}
                                {field.message && (
                                  <FormFeedback className={'d-block text-info'}>
                                    {field.message}
                                  </FormFeedback>
                                )}
                                {shouldShowError(field.key) && (
                                  <FormFeedback className="d-block">{getFieldError(field.key)}</FormFeedback>
                                )}
                              </FormGroup>
                            ) : null;
                          })()
                        ) : (
                          // Render empty column for spacing
                          <div className="form-group">&nbsp;</div>
                        )}
                      </Col>
                    ))}
                    </Row>
                  </div>
                );
              })
            ) : (
              visibleFields.map((field) => (
                // Conditionally render form fields based on user rights and field types
                shouldShowPasswordFields || (!['password', 'passwordRepeat'].includes(field.key)) ? (
                  <Col md={onlyOneField ? 12 : 6} key={field.key} className="mb-3">
                    <FormGroup>
                      <Label htmlFor={field.key}>
                        <span data-cy={'form-label-' + field.key}>{(field.key === 'password' && isEdit) ? t('changePassword') : (field.key === 'passwordRepeat' && isEdit) ? t('changePasswordRepeat') : (field.label) ? field.label : t(field.key)}</span>{field.required && <span className="text-danger">*</span>}
                      </Label>
                      {/* Render different input types based on field.type */}
                      {(field.type === 'date') ? (
                        <DatePicker
                          name={field.key}
                          id={field.key}
                          onChange={(value, formattedValue) => {
                            formik.setFieldValue(field.key, value); // Update field value in Formik
                          }}
                          onBlur={formik.handleBlur}
                          value={formik.values[field.key]}
                          invalid={shouldShowError(field.key)}
                          placeholder={field.placeholder || ''}
                          autoComplete="new-password"
                          maxDate={field.max ? field.max.format() : null}
                          disabled={field.isDisabled}
                          dateFormat={"DD/MM/YYYY"}
                          data-cy={'form-input-' + field.key}
                        />
                      ) : (field.type === 'select') ? 
                        ((isEdit && !hasRight(prefListName,"w", user)) || field.isDisabled ? (
                          // Display select as read-only input when user doesn't have write rights
                          <Input
                            type={'text'}
                            name={field.key}
                            id={field.key}
                            onChange={formik.handleChange}
                            onBlur={formik.handleBlur}
                            value={(field && field.fullOptions)
                                      ? (
                                        field.fullOptions.find((el) => 
                                          el.id == formik.values[field.key]
                                        ) && field.fullOptions.find((el) => 
                                          el.id == formik.values[field.key]
                                        ).lib
                                      ) ? field.fullOptions.find((el) => 
                                            el.id == formik.values[field.key]
                                          ).lib
                                        : formik.values[field.key]
                                      : (field && field.options)
                                        ? (
                                          field.options.find((el) => 
                                            el.id == formik.values[field.key]
                                          ) && field.options.find((el) => 
                                            el.id == formik.values[field.key]
                                          ).lib
                                        ) ? field.options.find((el) => 
                                            el.id == formik.values[field.key]
                                          ).lib
                                          : formik.values[field.key]
                                        : formik.values[field.key]
                                    }
                            invalid={shouldShowError(field.key)}
                            placeholder={field.placeholder || ''}
                            autoComplete='new-password'
                            disabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (isProfileView && user.id === initialValues.id) ? false : (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                            data-cy={'form-input-' + field.key}
                          />
                        ) : (
                            // Render normal select dropdown when user has write rights
                          <Select
                            name={field.key}
                            id={field.key}
                            placeholder={field.placeholder || ''}
                            className={"react-select-container" + (shouldShowError(field.key) ? ' is-invalid' : '')}
                            classNamePrefix="react-select"
                            onChange={(option) => handleDependentFieldUpdate(field.key, option ? option.value : null)}
                            onBlur={() => formik.setFieldTouched(field.key, true)}
                            value={formik.values[field.key] ? field.options
                              .map(option => ({
                                value: typeof option === 'object' ? option.id : option,
                                label: t(typeof option === 'object' ? option.label : option)
                              }))
                              .find(option => option.value === formik.values[field.key]) : null}
                            isDisabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                            data-cy={'form-input-' + field.key}
                            options={field.options
                              .filter(option => {
                                // Filter based on dependencies
                                if (field.isDependingOn) {
                                  if (formik.values[field.isDependingOn] != option[field.isDependingOn] && 
                                      formik.values[field.isDependingOn] != null && 
                                      formik.values[field.isDependingOn] != undefined && 
                                      formik.values[field.isDependingOn] != '') {
                                    return false;
                                  }
                                }

                                // Handle dependent fields
                                if (field.isDependingOnValue) {
                                  const dependingField = fields.find((el) => el.key == field.isDependingOnValue);
                                  const dependingFieldOptions = (dependingField && dependingField.options) ? dependingField.options : null;
                                  const dependingValue = (dependingFieldOptions && formik.values[field.isDependingOnValue]) 
                                    ? dependingFieldOptions.find((el) => el.id == formik.values[field.isDependingOnValue]) 
                                    : null;
                                  
                                  if((!dependingValue || (dependingValue && dependingValue[field.key] && dependingValue[field.key] != option.id)) && 
                                    (formik.values[field.isDependingOnValue] != null && 
                                      formik.values[field.isDependingOnValue] != undefined && 
                                      formik.values[field.isDependingOnValue] != '')) {
                                    return false;
                                  }
                                }

                                // Handle N-to-N relationships
                                if (field.isDependingOnArrayValue) {
                                  const dependingField = fields.find((el) => el.key == field.isDependingOnArrayValue);
                                  const dependingFieldOptions = (dependingField && dependingField.options) ? dependingField.options : null;
                                  const dependingValue = (dependingFieldOptions && formik.values[field.isDependingOnArrayValue]) 
                                    ? dependingFieldOptions.find((el) => el.id == formik.values[field.isDependingOnArrayValue]) 
                                    : null;
                                  const dependingArrayValues = (dependingValue) ? option[field.dependingOnArrayKey].includes(dependingValue.id) : null;
                                  
                                  if((!dependingValue || !dependingArrayValues) && 
                                    (formik.values[field.isDependingOnArrayValue] != null && 
                                      formik.values[field.isDependingOnArrayValue] != undefined && 
                                      formik.values[field.isDependingOnArrayValue] != '')) {
                                    return false;
                                  }
                                }

                                return true;
                              })
                              .map(option => ({
                                value: typeof option === 'object' ? option.id : option,
                                label: t(typeof option === 'object' ? option.label : option)
                              }))}
                            isClearable
                          />

                        )
                      ) : (field.type === 'selectArray') ? (
                        // Handle multi-select fields
                        <>
                          <Select
                            isMulti
                            name={field.key}
                            id={field.key}
                            options={field.options.map(option => ({ value: option.id, label: t(option.label) }))}
                            className={"react-select-container" + (shouldShowError(field.key) ? ' is-invalid' : '')}
                            classNamePrefix="react-select"
                            styles={customSelectStyles(shouldShowError(field.key))}
                            value={field.options.filter(option => formik.values[field.key].includes(option.id)).map(option => ({ value: option.id, label: t(option.label) }))}
                            onChange={selectedOptions => formik.setFieldValue(field.key, selectedOptions ? selectedOptions.map(option => option.value) : [])}
                            onBlur={() => formik.setFieldTouched(field.key, true)}
                            disabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (isProfileView && user.id === initialValues.id) ? false : (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                            placeholder={field.placeholder || ''}
                            data-cy={'form-input-' + field.key}
                          />
                          {formik.touched[field.key] && formik.errors[field.key] && (
                            <FormFeedback className="d-block">{formik.errors[field.key]}</FormFeedback>
                          )}
                        </>
                      ) : (field.type === 'button') ? (
                        <div className="d-flex justify-content-start mb-3">
                          <Button
                            type="button" // Important pour éviter la soumission du formulaire
                            color="primary"
                            onClick={field.onClick}
                            className="mt-2"
                            data-cy={'form-button-' + field.key}
                          >
                            {field.label}
                          </Button>
                        </div>
                      ) : (
                        // Handle text inputs and password fields
                        <div className={(field.type == 'password') ? "passwordField" : ''}>
                          <InputGroup>
                            <Input
                              type={
                                /* Change input type to text if showPassword or showPasswordRepeat are true */
                                field.key == 'password' ? ((field.type !== 'password') 
                                                          ? field.type 
                                                          : (showPassword ? 'text': 'password'))
                                                        : ((field.type !== 'password') 
                                                          ? (field.type == 'float') 
                                                            ? 'number'
                                                            : field.type 
                                                          : (showPasswordRepeat ? 'text': 'password'))}
                              name={field.key}
                              id={field.key}
                              className={field.key}
                              onChange={(e) => {
                                formik.handleChange(e);
                                // Vérifier si le champ a une configuration autoSelect
                                if (field.autoSelect) {
                                  handlePostalAutoSelect(field, e.target.value);
                                }
                              }}
                              onBlur={formik.handleBlur}
                              onKeyDown={(e) => {
                                if(field.type == 'number' || field.type == 'float') {
                                  handleNumberKeyDown(e);
                                }
                              }}
                              step={field.type == 'float' ? (field.step || 0.5) : undefined}
                              value={t(formik.values[field.key])}
                              invalid={shouldShowError(field.key)}
                              placeholder={field.placeholder || ''}
                              autoComplete='new-password'
                              disabled={(field.isDisabled) ? true : (!isEdit) ? false : (field.key != 'password' && field.key != 'passwordRepeat') ? (isProfileView && user.id === initialValues.id) ? false : (prefListName == "user" && initialValues && initialValues.groupRights) ? !hasAllRights(initialValues.groupRights, user) : !hasRight(prefListName,"w", user) : (!shouldShowPasswordFields) ? true : false}
                              data-cy={'form-input-' + field.key}
                            />
                            {/* Show password toggle button for password field */}
                            {(field.key === 'password') && (
                              <span className='passwordVisibility' onClick={() => setShowPassword(!showPassword)}>
                                <a id="projTogglePasswordVisibility" style={{ padding: 0 }}>
                                  <i className={showPassword ? 'mdi mdi-eye' : 'mdi mdi-eye-off'} />
                                </a>
                              </span>
                            )}
                            {/* Show password toggle button for password repeat field */}
                            {(field.key === 'passwordRepeat') && (
                              <span className='passwordVisibility' onClick={() => setShowPasswordRepeat(!showPasswordRepeat)}>
                                <a id="projTogglePasswordVisibility" style={{ padding: 0 }}>
                                  <i className={showPasswordRepeat ? 'mdi mdi-eye' : 'mdi mdi-eye-off'} />
                                </a>
                              </span>
                            )}
                          </InputGroup>
                        </div>
                      )}
                      {/* Password field specific feedback and instructions */}
                      {field.message && (
                        <FormFeedback className={'d-block text-info'}>
                          {field.message}
                        </FormFeedback>
                      )}
                      {shouldShowError(field.key) && (
                        <FormFeedback className="d-block">{getFieldError(field.key)}</FormFeedback>
                      )}
                    </FormGroup>
                  </Col>
                ) : null
              ))
            )}
          </Row>
          {/* Render form action buttons */}
          {!isDetailMode && (
            isSignupForm ? (
              // Boutons pour le formulaire d'inscription
              <div className="signUpFooter">
                <button className="btn btn-secondary btn-block" type="button" onClick={handleCancelFunction}>
                  {t('back')}
                </button>
                <button 
                  className="btn btn-primary btn-block" 
                  type="submit"
                  data-cy="signup-submit"
                >
                  {isLoading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                    </>
                  ) : (
                    t('register')
                  )}
                </button>
              </div>
            ) : (
              // Boutons standard pour les autres formulaires
              !withoutCancel ? (
                <div className="d-flex justify-content-end">
                  <Button id="projCancelBtn" color="secondary" type="button" onClick={handleCancel} data-cy="form-button-cancel">
                    {(isChecklistDo) ? t('back') : t('cancel')}
                  </Button>
                  <Button id="projCreateBtn" color="primary" type="submit" data-cy="form-button-create" className='justify-content-center d-flex align-items-center'>
                    {
                      isLoading ? (
                        <>
                          <Spinner size="sm"/>
                        </>
                      ) : (
                        (isChecklistDo) ? t('startChecklist') : t('submit')
                      )
                    }
                  </Button>
                  {
                  (submitAndNavigatBtn) && (
                    <Button id="projCreateBtn" color="primary" onClick={ () => {setNavigateDirectly(true)}} type="submit" data-cy="form-button-create" className='justify-content-center d-flex align-items-center'>
                      {
                        isLoading ? (
                          <>
                            <Spinner size="sm"/>
                          </>
                        ) : (
                          t(submitAndNavigatLabel)
                        )
                      }
                    </Button>
                  )
                  }
                </div>
              ) : (
                <div className="d-flex justify-content-end">
                  <Button id="projCreateBtn" color="primary" type="submit" data-cy="form-button-create">
                    {(isChecklistDo) ? t('startChecklist') : t('submit')}
                  </Button>
                </div>
              )
            )
          )
          }
        </Form>
      </div>
      <ToastContainer
        containerId="toastContainerDynamicForm"
      />
    </div>
  );
};

export default DynamicForm;